import React, {useCallback, useRef, useState} from 'react';
import {Alert, Box, Typography} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface ImageDropZoneProps {
    onFilesSelected: (files: File[]) => void;
    disabled?: boolean;
    accept?: string;
    multiple?: boolean;
    label?: string;
    maxSizeMb?: number;
}

const ImageDropZone: React.FC<ImageDropZoneProps> = ({
    onFilesSelected,
    disabled = false,
    accept = 'image/jpeg,image/png,image/webp',
    multiple = true,
    label = 'Bilder hierher ziehen oder klicken zum Auswählen',
    maxSizeMb = 20,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleFiles = useCallback((files: FileList | null) => {
        if (!files || disabled) return;
        setError(null);
        const validFiles: File[] = [];
        const maxSize = maxSizeMb * 1024 * 1024;
        const acceptedTypes = accept.split(',');
        let wrongType = 0;
        let tooLarge = 0;

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (!acceptedTypes.includes(file.type)) {
                wrongType++;
                continue;
            }
            if (file.size > maxSize) {
                tooLarge++;
                continue;
            }
            validFiles.push(file);
        }

        const errors: string[] = [];
        if (wrongType > 0) {
            errors.push(
                `${wrongType} Datei(en) übersprungen (falsches Format)`,
            );
        }
        if (tooLarge > 0) {
            errors.push(
                `${tooLarge} Datei(en) übersprungen (über ${maxSizeMb} MB)`,
            );
        }
        if (errors.length > 0) {
            setError(errors.join('. '));
        }

        if (validFiles.length > 0) {
            onFilesSelected(validFiles);
        }
    }, [onFilesSelected, disabled, accept, maxSizeMb]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        handleFiles(e.dataTransfer.files);
    }, [handleFiles]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        if (!disabled) setIsDragOver(true);
    }, [disabled]);

    const handleDragLeave = useCallback(() => {
        setIsDragOver(false);
    }, []);

    const handleClick = useCallback(() => {
        if (!disabled && inputRef.current) {
            inputRef.current.click();
        }
    }, [disabled]);

    return (
        <Box>
            <Box
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={handleClick}
                sx={{
                    border: '2px dashed',
                    borderColor: isDragOver
                        ? 'primary.main'
                        : (disabled ? 'grey.300' : 'grey.400'),
                    borderRadius: 2,
                    padding: 3,
                    textAlign: 'center',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    backgroundColor: isDragOver
                        ? 'action.hover' : 'transparent',
                    opacity: disabled ? 0.5 : 1,
                    transition: 'all 0.2s ease',
                    '&:hover': disabled ? {} : {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover',
                    },
                }}
            >
                <input
                    ref={inputRef}
                    type={'file'}
                    accept={accept}
                    multiple={multiple}
                    onChange={e => handleFiles(e.target.files)}
                    style={{display: 'none'}}
                />
                <CloudUploadIcon
                    sx={{
                        fontSize: 40,
                        color: disabled ? 'grey.400' : 'grey.600',
                    }}
                />
                <Typography
                    variant={'body2'}
                    sx={{marginTop: 1, color: 'text.secondary'}}
                >
                    {label}
                </Typography>
                <Typography
                    variant={'caption'}
                    sx={{color: 'text.disabled'}}
                >
                    JPG, PNG, WebP - max. {maxSizeMb} MB pro Bild
                </Typography>
            </Box>
            {error && (
                <Alert
                    severity={'warning'}
                    sx={{mt: 1}}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}
        </Box>
    );
};

export default ImageDropZone;
